import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { DollarSign, TrendingUp, Check, X } from "lucide-react";
import KOPipelineStep from "./KOPipelineStep";
import { allKeywords, mockBudget } from "./mockData";

const KOBudgetAnalysis = () => {
  const tableRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(tableRef, { once: true, margin: "-50px" });

  const displayKeywords = allKeywords.slice(0, 12);

  return (
    <section className="py-24 px-4 bg-muted/30">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Badge className="px-3 py-1 text-sm bg-primary/10 text-primary border-primary/20 mb-4">
              The Upfixe Method
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              How Budget-Smart Keyword Selection Works
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Five automated steps transform a raw keyword list into a budget-optimised, thematically
              organised campaign that delivers maximum reach within your financial constraints.
            </p>
          </motion.div>
        </div>

        <KOPipelineStep step={1} title="Analyse Your Budget Against Bid Estimates">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left: Budget inputs */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
                <h4 className="font-semibold mb-4 text-lg">Your Campaign Settings</h4>
                <div className="space-y-4">
                  <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
                    <div className="flex items-center gap-3 mb-1">
                      <DollarSign className="h-5 w-5 text-primary" />
                      <span className="text-sm text-muted-foreground">Daily Budget</span>
                    </div>
                    <span className="text-3xl font-bold">${mockBudget.dailyBudget}</span>
                    <span className="text-muted-foreground ml-1">/day</span>
                  </div>
                  <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
                    <div className="flex items-center gap-3 mb-1">
                      <TrendingUp className="h-5 w-5 text-primary" />
                      <span className="text-sm text-muted-foreground">Target CPA</span>
                    </div>
                    <span className="text-3xl font-bold">${mockBudget.targetCPA}</span>
                    <span className="text-muted-foreground ml-1">/conversion</span>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-800">
                    <strong>Rule:</strong> Only bid on keywords where the estimated CPC is less than
                    your target CPA (${mockBudget.targetCPA}). Keywords costing more per click than
                    your target conversion cost will never be profitable.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Right: Keyword comparison table */}
            <motion.div
              className="lg:col-span-3"
              ref={tableRef}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-muted/50 border-b border-border">
                  <h4 className="font-semibold">Keyword Bid Comparison</h4>
                  <p className="text-xs text-muted-foreground">
                    Comparing estimated CPC against your ${mockBudget.targetCPA} target CPA
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {displayKeywords.map((kw, index) => (
                    <motion.div
                      key={kw.keyword}
                      className={`px-6 py-3 flex items-center justify-between text-sm ${
                        kw.affordable ? "" : "bg-red-50/50"
                      }`}
                      initial={{ opacity: 0, x: 10 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.3, delay: index * 0.06 }}
                    >
                      <span className="font-medium flex-1">{kw.keyword}</span>
                      <div className="flex items-center gap-4">
                        <span
                          className={`font-bold ${
                            kw.affordable ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          ${kw.bidEstimate.toFixed(2)}
                        </span>
                        <span className="text-xs text-muted-foreground w-16 text-right">
                          {(kw.searchVolume / 1000).toFixed(1)}K vol
                        </span>
                        {kw.affordable ? (
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <X className="h-4 w-4 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
                <div className="px-6 py-3 bg-muted/30 text-xs text-muted-foreground flex justify-between">
                  <span>Showing 12 of 247 keywords analysed</span>
                  <span className="font-medium">
                    <span className="text-green-600">18 affordable</span> · <span className="text-red-500">8 filtered</span>
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* SEO text */}
          <motion.p
            className="mt-8 text-muted-foreground leading-relaxed max-w-4xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Upfixe pulls real-time bid estimates from the Google Ads API for every keyword candidate.
            Each keyword's estimated cost-per-click is compared against your target CPA — the maximum
            you're willing to pay for a conversion. If a keyword costs more per click than your entire
            conversion target, it's mathematically impossible to achieve positive ROI on that keyword.
            This simple but powerful filter is the foundation of budget-smart advertising.
          </motion.p>
        </KOPipelineStep>
      </div>
    </section>
  );
};

export default KOBudgetAnalysis;
